import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { routerCancelAction, routerErrorAction, routerNavigatedAction, routerNavigationAction } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { timer } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

import { RootState } from '@root/app.state';

import { CurrentUserService } from './auth/current-user.service';
import * as PlatformActions from './platform.actions';
import { selectPortalSettings, selectUser } from './platform.selectors';
import { PortalSettingsService } from './portal-settings/portal-settings.service';
import { extractShowProgressBar } from './routing/params-helper';

@Injectable()
export class PlatformEffects {
  loadPortalSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlatformActions.loadPortalSettings),
      switchMap(() =>
        this.portalSettingsService.loadPortalSettings().pipe(
          mergeMap((dto) => [PlatformActions.loadPortalSettingsSucceeded({ portalSettings: dto })]),
          catchError((err: HttpErrorResponse) => [
            PlatformActions.loadPortalSettingsFailed({
              status: err.status,
              errors: err.error,
            }),
          ]),
        ),
      ),
    ),
  );

  loadCurrentUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlatformActions.loadCurrentUser),
      switchMap(() =>
        this.currentUserService.getCurrentUser().pipe(
          mergeMap((dto) => [PlatformActions.loadCurrentUserSucceeded({ user: dto })]),
          catchError((err: HttpErrorResponse) => [
            PlatformActions.loadCurrentUserFailed({
              status: err.status,
              errors: err.error,
            }),
          ]),
        ),
      ),
    ),
  );

  loadCurrentUserSucceeded$ = createEffect(
    () => () =>
      this.actions$.pipe(
        ofType(PlatformActions.loadCurrentUserSucceeded),
        tap((dto) => this.translateService.use(dto.user.language === 'English' ? 'en-US' : 'de-CH')),
      ),
    { dispatch: false },
  );

  postCurrentUserLanguage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlatformActions.postCurrentUserLanguage),
      switchMap(({ language }) =>
        this.currentUserService.postCurrentUserLanguage(language).pipe(
          mergeMap((dto) => [PlatformActions.loadCurrentUserSucceeded({ user: dto })]),
          catchError((err: HttpErrorResponse) => [
            PlatformActions.loadCurrentUserFailed({
              status: err.status,
              errors: err.error,
            }),
          ]),
        ),
      ),
    ),
  );

  openDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlatformActions.openDialog),
      tap(({ componentOrTemplateRef, config }) => this.matDialog.open(componentOrTemplateRef, config)),
      map(({ config }) => PlatformActions.dialogOpened({ id: config!.id! })),
    ),
  );

  closeDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlatformActions.closeDialog),
      map(({ id, data }) => ({ dialogRef: this.matDialog.getDialogById(id), data })),
      tap(({ dialogRef, data }) => dialogRef!.close(data)),
      mergeMap(({ dialogRef, data }) =>
        dialogRef!.afterClosed().pipe(map(() => PlatformActions.dialogClosed({ id: dialogRef!.id, data }))),
      ),
    ),
  );

  triggerShowProgressBar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(routerNavigationAction),
      filter((a) => extractShowProgressBar(a.payload.routerState.root)),
      map(() => PlatformActions.showProgressBar()),
    ),
  );

  triggerHideProgressBar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(routerNavigatedAction, routerCancelAction, routerErrorAction),
      filter((a) => extractShowProgressBar(a.payload.routerState.root)),
      map(() => PlatformActions.hideProgressBar()),
    ),
  );

  showProgressBar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlatformActions.showProgressBar),
      switchMap(() =>
        timer(0, 500).pipe(
          map(() => PlatformActions.increaseProgressBar()),
          takeUntil(this.actions$.pipe(ofType(PlatformActions.hideProgressBar))),
        ),
      ),
    ),
  );

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PlatformActions.logout),
        tap(() => this.oauthService.logOut()),
      ),
    { dispatch: false },
  );

  timedLogout$ = createEffect(() =>
    this.actions$.pipe(
      filter((action) => action.type !== PlatformActions.logout.type),
      withLatestFrom(this.store$),
      filter(([, store]) => !!selectUser(store).data),
      map(([, store]) => selectPortalSettings(store)),
      switchMap((settings) =>
        timer(1000 * 60 * settings.data.portalConfig.maximumIdleTimeMinutes).pipe(map(() => PlatformActions.logout())),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private translateService: TranslateService,
    private portalSettingsService: PortalSettingsService,
    private currentUserService: CurrentUserService,
    private matDialog: MatDialog,
    private oauthService: OAuthService,
    private store$: Store<RootState>,
  ) {}
}
